/**
 * Course — Certificate panel
 *
 * Completion banner shown to an authenticated learner who has finished a
 * course that issues a certificate. Presentational + controlled: the page
 * owns the certificate state, generation, and download; this renders the
 * success band and an adaptive action button.
 *
 * Button states (driven by the page's certificate state machine):
 *  - generating       → spinner, disabled
 *  - just generated   → transient "Certificate ready" confirmation
 *  - certificate ready→ "Download certificate"
 *  - no certificate   → "Get certificate" (generates, then auto-downloads)
 */

import { Certificate } from '../../apis/api-client';
import { CertificateIcon, CheckCircleIcon, DownloadIcon } from '../icons';
import { Box, Button, CircularProgress, Typography } from '../ui';

interface CourseCertificatePanelProps {
  certificate: Certificate | null;
  generating: boolean;
  justGenerated: boolean;
  onGenerate: () => void;
  onDownload: () => void;
}

export default function CourseCertificatePanel({ certificate, generating, justGenerated, onGenerate, onDownload }: CourseCertificatePanelProps) {
  const action = (() => {
    if (generating) {
      return (
        <Button variant="contained" color="primary" disabled startIcon={<CircularProgress size={18} color="inherit" />}>
          Generating…
        </Button>
      );
    }
    if (justGenerated) {
      return (
        <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={onDownload}>
          Certificate ready
        </Button>
      );
    }
    if (certificate) {
      return (
        <Button variant="contained" color="primary" startIcon={<DownloadIcon />} onClick={onDownload}>
          Download certificate
        </Button>
      );
    }
    return (
      <Button variant="contained" color="primary" startIcon={<CertificateIcon />} onClick={onGenerate}>
        Get certificate
      </Button>
    );
  })();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2.5,
        borderRadius: 2,
        bgcolor: 'success.softBg',
        border: '1px solid',
        borderColor: 'success.light',
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 1.5,
          bgcolor: 'background.paper',
          color: 'success.dark',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <CertificateIcon />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'success.dark' }}>
          You've completed this course
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {certificate ? 'Your certificate of completion is ready to download.' : 'Generate your certificate of completion.'}
        </Typography>
      </Box>
      <Box sx={{ flexShrink: 0 }}>{action}</Box>
    </Box>
  );
}
